import Breadcrumbs from "@/components/design-system/breadcrumbs";
import { Button } from "@/components/design-system/button";
import { DatePicker } from "@/components/design-system/date-picker";
import { Input } from "@/components/design-system/input";
import SelectWithRadio from "@/components/design-system/SelectWithRadio";
import { isAllSchoolsSelected, selectSchoolId, selectUser } from "@/store/slices/authSlice";
import {
  createNewTeacher,
  fetchTeacherById,
  selectTeacherActionLoading,
  updateExistingTeacher,
} from "@/store/slices/teacherSlice";
import { TeacherModel } from "@/types/teacher.model";
import { successToast } from "@/utils/constants";
import { AppDispatch } from "@store";
import moment from "moment";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useParams } from "react-router-dom";

interface AddTeacherProps {
  readonly viewMode?: boolean;
  readonly editMode?: boolean;
}

interface TeacherFormData {
  teacherName: string;
  dob: string;
  age: string;
  gender: string;
  emailAddress: string;
  contactNumber: string;
  schoolId: string;
  // grade: string;
}

interface FieldConfig {
  id: keyof TeacherFormData;
  label: string;
  placeholder: string;
  type?: string;
  isSelect?: boolean;
  isDatePicker?: boolean;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  required?: boolean;
  readOnly?: boolean;
  maxLength?: number;
}

export default function AddTeacher({
  viewMode = false,
  editMode = false,
}: AddTeacherProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const actionLoading = useSelector(selectTeacherActionLoading);

  const [isLoading, setIsLoading] = useState(viewMode || editMode);

  const selectedSchoolId = useSelector(selectSchoolId);
  const isAllSchools = useSelector(isAllSchoolsSelected);
  const loggedUser = useSelector(selectUser);
  const schoolList: any[] = loggedUser?.school_list?.filter((s) => s.type !== "all") || [];
  const [schoolId, setSchoolId] = useState<any>("");

  useEffect(() => {
    if (!viewMode && !editMode) {
      const schoolIdFromParams = searchParams.get("schoolId");
      if (schoolIdFromParams) {
        setSchoolId(schoolIdFromParams);
      } else if (selectedSchoolId && !isAllSchools) {
        setSchoolId(selectedSchoolId);
      }
    }
  }, [selectedSchoolId, isAllSchools, viewMode, editMode]);

  useEffect(() => {
    if (schoolId) {
      setValue("schoolId", schoolId);
    }
  }, [schoolId]);

  // Form setup with react-hook-form
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm<TeacherFormData>({
    defaultValues: {
      teacherName: "",
      dob: "",
      age: "",
      gender: "",
      emailAddress: "",
      contactNumber: "",
      schoolId: "",
      // grade: "",
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
  });

  // Calculate age from date of birth
  const calculateAge = (dob: string): string => {
    if (!dob) return "";
    const birthDate = moment(dob);
    if (!birthDate.isValid()) return "";
    return moment().diff(birthDate, "years").toString();
  };

  // Update age whenever DOB changes
  useEffect(() => {
    const dob = watch("dob");
    if (dob) {
      setValue("age", calculateAge(dob), { shouldValidate: true });
      // Explicitly trigger validation for age field
    }
  }, [watch("dob"), setValue]);

  // Fetch teacher data when in view or edit mode
  useEffect(() => {
    if ((viewMode || editMode) && id) {
      dispatch(fetchTeacherById(id))
        .unwrap()
        .then((data) => {
          setIsLoading(false);
          setFormValues(data);
        })
        .catch((error) => {
          console.error("Error fetching teacher:", error);
          // errorToast("Failed to fetch teacher details", "");
          setIsLoading(false);
        });
    }
  }, [id, viewMode, editMode]);

  const setFormValues = (teacherData: TeacherModel) => {
    if (teacherData) {
      setValue("teacherName", teacherData.name);

      // Format the date to YYYY-MM-DD if it exists
      if (teacherData.dob) {
        // Check if the date is already in YYYY-MM-DD format
        if (teacherData.dob.match(/^\d{4}-\d{2}-\d{2}$/)) {
          setValue("dob", teacherData.dob);
        } else if (teacherData.dob.match(/^\d{2}-\d{2}-\d{4}$/)) {
          // Convert from DD-MM-YYYY to YYYY-MM-DD
          const [day, month, year] = teacherData.dob.split("-");
          setValue("dob", `${year}-${month}-${day}`);
        } else {
          // Convert from any other format to YYYY-MM-DD
          const formattedDate = moment(teacherData.dob).format("YYYY-MM-DD");
          setValue("dob", formattedDate);
        }
      } else {
        setValue("dob", "");
      }

      // Age will be calculated automatically from DOB
      setValue("gender", teacherData.gender);
      setValue("emailAddress", teacherData.email);
      setValue("contactNumber", teacherData.contact_number);
      setValue("schoolId", teacherData.schoolId || teacherData.school_id || "");
    }
  };

  // Form submission handler
  const onFormSubmit = async (data: TeacherFormData) => {
    try {
      // Transform form data to match the TeacherModel interface
      // The date should already be in YYYY-MM-DD format from the DatePicker
      let formattedDob = data.dob;
      // If for some reason we still have DD-MM-YYYY format, convert it
      if (data.dob && data.dob.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = data.dob.split("-");
        formattedDob = `${year}-${month}-${day}`;
      }

      const teacherData: Partial<TeacherModel> = {
        name: data.teacherName,
        dob: formattedDob,
        age: parseInt(data.age || "0"),
        gender: data.gender,
        email: data.emailAddress,
        contact_number: data.contactNumber,
        schoolId: data.schoolId || undefined,
        // grade: data.grade,
        // is_active: true,
        // students: 0, // This will be managed by the backend
      };

      if (editMode && id) {
        // Update existing teacher
        await dispatch(updateExistingTeacher({ id, teacherData })).unwrap();
        successToast(`Teacher updated successfully`, "");
        navigateToBack();
      } else {
        // Create new teacher
        await dispatch(createNewTeacher(teacherData)).unwrap();
        successToast(`Teacher added successfully`, "");
        navigateToBack();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // errorToast(
      //   "Error",
      //   `Failed to ${editMode ? "update" : "create"} teacher. Please try again.`
      // );
    }
  };

  const navigateToBack = () => {
    navigate(schoolId ? `/user-management/teacher?schoolId=${schoolId}` : "/user-management/teacher");
  };

  // Form validation rules
  const getValidationRules = (field: FieldConfig) => {
    const rules: Record<string, unknown> = {};

    if (field.required) {
      switch (field.id) {
        case "teacherName":
          rules.required = "Please enter the teacher name.";
          break;
        case "dob":
          rules.required = "Please enter the teacher dob.";
          break;
        case "age":
          rules.required = "Please enter the teacher age.";
          break;
        case "gender":
          rules.required = "Please enter the teacher gender.";
          break;
        case "emailAddress":
          rules.required = "Please enter a valid email address.";
          break;
        case "contactNumber":
          rules.required = "Please enter a valid mobile number.";
          break;
        case "schoolId":
          rules.required = "Please select a valid school.";
          break;

        default:
          rules.required = `${field.label} is required`;
      }
    }

    if (field.id === "emailAddress") {
      rules.pattern = {
        value: /^\S+@\S+\.\S+$/,
        message: "Please enter a valid email address",
      };
    }

    if (field.id === "contactNumber") {
      rules.pattern = {
        value: /^[0-9]{10}$/,
        message: "Please enter a valid mobile number",
      };
    }

    if (field.id === "age") {
      rules.min = {
        value: 1,
        message: "Age must be greater than 0",
      };
      rules.max = {
        value: 100,
        message: "Age must be less than 100",
      };
    }

    return rules;
  };

  // Function to render each field based on its configuration
  const renderField = (field: FieldConfig) => (
    <div key={field.id} className="w-full">
      {field.isSelect ? (
        <>
          <SelectWithRadio
            id={field.id}
            label={field.label}
            placeholder={field.placeholder}
            options={field.options || []}
            value={watch(field.id)}
            onChange={(value: string) => {
              setValue(field.id, value, { shouldValidate: true });
              if (field.id === "schoolId") {
                setSchoolId(value);
              }
              // Manually trigger validation after setting value
              trigger(field.id);
            }}
            showAbove={false}
            error={errors[field.id]?.message}
            disabled={viewMode}
            required={field.required}
            maxHeight="300px"
          />
          <input
            type="hidden"
            {...register(field.id, getValidationRules(field))}
          />
        </>
      ) : field.isDatePicker ? (
        <>
          <DatePicker
            id={field.id}
            label={field.label}
            placeholder={field.placeholder}
            value={watch(field.id) ? (watch(field.id) as string) : ""}
            onChange={(date) => {
              if (!date) {
                setValue("age", "");
              }
              setValue(field.id, date, {
                shouldValidate: true,
              });
              // Manually trigger validation after setting value
              trigger(field.id);
            }}
            maxDate={moment().format("YYYY-MM-DD")}
            error={errors[field.id]?.message as string}
            readOnly={viewMode}
            required={field.required}
          />
          <input
            type="hidden"
            {...register(field.id, getValidationRules(field))}
          />
        </>
      ) : (
        <Input
          id={field.id}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type || "text"}
          {...register(field.id, getValidationRules(field))}
          errorMessage={errors[field.id]?.message}
          min={field.min}
          max={field.max}
          required={field.required}
          maxLength={field.maxLength}
          disabled={
            viewMode ||
            (editMode && field.id === "emailAddress") ||
            field.readOnly
          }
          readOnly={
            viewMode ||
            (editMode && field.id === "emailAddress") ||
            field.readOnly
          }
        />
      )}
    </div>
  );

  const teacherFields: FieldConfig[] | any[] = [
    {
      id: "teacherName",
      label: "Teacher Name",
      placeholder: "Enter Teacher Name",
      required: true,
      maxLength: 60,
    },
    {
      id: "dob",
      label: "DOB",
      placeholder: "Enter Date of Birth",
      isDatePicker: true,
      required: true,
    },
    {
      id: "age",
      label: "Age",
      placeholder: "Age",
      min: 1,
      max: 100,
      required: true,
      readOnly: true,
    },
    {
      id: "gender",
      label: "Gender",
      placeholder: "Select Gender",
      isSelect: true,
      required: true,
      options: [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
        { value: "other", label: "Other" },
      ],
    },
    {
      id: "emailAddress",
      label: "Email Address",
      placeholder: "Enter Email Address",
      type: "email",
      required: true,
    },
    {
      id: "contactNumber",
      label: "Contact Number",
      placeholder: "Your Contact Number",
      type: "tel",
      required: true,
    },
    ...(isAllSchools
      ? [
        {
          id: "schoolId",
          label: "School",
          placeholder: "Select School",
          isSelect: true,
          required: true,
          options: schoolList?.map((school) => ({
            value: school.id,
            label: school.name,
          })),
        },
      ]
      : []),
    // {
    //   id: "grade",
    //   label: "Grade",
    //   placeholder: "Select Grade",
    //   isSelect: true,
    //   required: false,
    //   options: grades.map((grade) => ({ value: grade.id, label: grade.grade })),
    // },
  ];

  const handleCancel = () => {
    navigateToBack();
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {isLoading ? (
        <div className="bg-white rounded-[12px] h-[calc(100vh-120px)] flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-t-[#5C88C5] border-r-[#5C88C5] border-b-[#5C88C5] border-l-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-gray-600">Loading teacher details...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-[12px] h-full">
            <form
              onSubmit={handleSubmit(onFormSubmit)}
              className="w-full h-full flex flex-col"
            >
              {/* Student Details Section */}
              <div className={`px-[32px] py-[33px] flex flex-col flex-1`}>
                <div className="mb-8">
                  <div className="">
                    <Breadcrumbs
                      title={
                        viewMode
                          ? "View Teacher"
                          : editMode
                            ? "Edit Teacher"
                            : "Add Teacher"
                      }
                      onClick={() => navigateToBack()}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {teacherFields.map(renderField)}
                  </div>
                </div>
              </div>

              {/* Form actions - Fixed at bottom */}
              <div className="flex h-[98px] border px-[32px] justify-end space-x-4 border-t border-gray-200 pt-6 shadow-[0px_4px_24px_8px_#0000001F]">
                {viewMode ? (
                  <Button
                    id="view-student-close"
                    type="button"
                    className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                    variant="primary"
                    onClick={handleCancel}
                  >
                    Close
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      className="h-[48px] w-[161px]"
                      variant="primary-outline"
                      onClick={handleCancel}
                      disabled={actionLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="h-[48px] w-[161px] rounded-[8px] bg-[#5C88C5]"
                      variant="primary"
                      disabled={!isValid || actionLoading}
                      loading={actionLoading}
                    >
                      {editMode ? "Update" : "Add"}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
